import pacisLogo from 'assets/pacis/logo.png'
import pacisLogoTransparent from 'assets/pacis/logo-transparent.png'
import pacisAuthImage from 'assets/shared/pacisInsurance.png'
import type { TenantConfig, TenantSeedTheme } from '../types'
import {
  generateTheme,
  generateDarkTheme,
  generateColors
} from '../generateTheme'

const seed: TenantSeedTheme = {
  primary: '210 80% 45%',
  secondary: '200 70% 50%',
  emailPrimaryHex: '#1565C0',
  emailSecondaryHex: '#42A5F5'
}

const pacisConfig: TenantConfig = {
  id: 'pacis',
  name: 'Pacis Insurance',
  tagline: 'Trusted Insurance Partner',
  logo: pacisLogo,
  logoTransparent: pacisLogoTransparent,
  authImage: pacisAuthImage,
  authBrandBackground: '#eef4fb',
  favicon: '/favicon.png',
  websiteUrl: 'https://pacis.co.ke',
  supportEmail: 'support@pacis.co.ke',
  supportPhone: '+254 700 000 000',
  twitterHandle: '@PacisInsurance',
  theme: generateTheme(seed),
  darkTheme: generateDarkTheme(seed),
  colors: generateColors('#184679', '#FDDB00'),
  content: {
    heroTitle: 'Motor Insurance',
    heroTitleAccent: 'You Can Trust',
    heroDescription:
      'Get your vehicle insured with Pacis — backed by 30+ years of experience. Compare plans, get a quote and buy your motor policy online in minutes.',
    ratingText: '4.7/5 from 50,000+ customers',
    stats: [
      { value: '50K+', label: 'Policyholders' },
      { value: '30+', label: 'Years of Trust' },
      { value: '24/7', label: 'Claims Support' },
      { value: '98%', label: 'Satisfaction Rate' }
    ],
    howItWorksTitle: 'How It Works',
    howItWorksSubtitle:
      'Three simple steps to get your vehicle insured with Pacis.',
    howItWorks: [
      {
        step: '01',
        title: 'Share Your Details',
        description:
          'Enter your vehicle information and personal details in our quick online form.'
      },
      {
        step: '02',
        title: 'Choose Your Plan',
        description:
          'Select from Comprehensive, Third Party Fire & Theft or Third Party Only — whatever suits you.'
      },
      {
        step: '03',
        title: 'Get Your Policy',
        description:
          'Pay securely and receive your motor insurance certificate right away.'
      }
    ],
    whyChooseTitle: 'Why Choose Pacis?',
    whyChooseSubtitle:
      'Decades of motor insurance expertise, now available at your fingertips. Fast quotes, fair prices, reliable claims.',
    features: [
      {
        title: 'Trusted Legacy',
        description:
          'Over 30 years of providing dependable motor insurance across East Africa.',
        badge: '30+',
        icon: 'shield'
      },
      {
        title: 'Fast Claims',
        description:
          'Streamlined claims processing to get you back on the road quickly.',
        badge: '48H',
        icon: 'clock'
      },
      {
        title: 'Dedicated Advisors',
        description:
          'Personal insurance advisors available to guide your every decision.',
        badge: '24/7',
        icon: 'users'
      },
      {
        title: 'Flexible Plans',
        description:
          'Comprehensive, third-party or third-party fire & theft — pick what fits.',
        badge: '100%',
        icon: 'document'
      }
    ],
    productsTitle: 'Insurance Products',
    productsSubtitle:
      'Motor insurance is our core offering, with more products on the way',
    products: [],
    testimonialsTitle: 'What Our Customers Say',
    testimonialsSubtitle:
      'Trusted by 50,000+ policyholders across East Africa.',
    testimonials: [
      {
        name: 'Grace Wanjiku',
        role: 'Private Car Owner',
        quote:
          'Pacis has been my insurer for 5 years. Their claims process is transparent and hassle-free.',
        rating: 5
      },
      {
        name: 'David Kamau',
        role: 'Business Owner',
        quote:
          'Insured my entire fleet with Pacis. The dedicated account manager makes everything simple.',
        rating: 5
      },
      {
        name: 'Lucy Njeri',
        role: 'First-time Buyer',
        quote:
          'Got my first motor policy with Pacis. The advisors walked me through every option clearly.',
        rating: 4
      }
    ],
    ctaTitle: 'Get Your Vehicle Covered Today',
    ctaText:
      'Join 50,000+ policyholders who trust Pacis for reliable, affordable motor insurance.',
    ctaButtonText: 'Get a Quote',
    faqTitle: 'Frequently Asked Questions',
    faqSubtitle: 'Common questions about motor insurance with Pacis.',
    faqItems: [
      {
        question: 'What motor insurance plans does Pacis offer?',
        answer:
          'We offer Comprehensive, Third Party Fire & Theft, and Third Party Only covers for all vehicle types.'
      },
      {
        question: 'How long does it take to get my policy?',
        answer:
          'Once payment is confirmed, your policy certificate is issued within minutes via email.'
      },
      {
        question: 'How do I make a claim?',
        answer:
          'Report the incident through your online dashboard or call our 24/7 claims hotline. We guide you through every step.'
      },
      {
        question: 'Can I pay in instalments?',
        answer:
          'Yes. Pacis offers flexible payment plans so you can spread your premium over the policy period.'
      }
    ],
    shareMessage: 'I am now insured with Pacis Insurance!',
    copyrightHolder: 'Pacis Insurance'
  },
  seo: {
    title: 'Pacis Insurance — Compare & Buy Insurance Online in Kenya',
    description:
      'Pacis Insurance, your trusted insurance partner. Compare quotes and buy motor, health and more insurance cover online in minutes.',
    keywords: [
      'Pacis Insurance',
      'insurance Kenya',
      'compare insurance quotes',
      'motor insurance',
      'buy insurance online'
    ],
    locale: 'en_KE',
    areaServed: ['KE']
  },
  storagePrefix: 'pacis',
  minVehicleValue: 500000
}

export default pacisConfig
